<template>
  <section class="h-full pb-12 overflow-hidden">
    <div v-if="status === 'idle' || status === 'pending'" class="container mb-8">
      <h2 class="font-script text-4xl text-secondary">
        Neuigkeiten &amp; Inspiration
      </h2>
      <p class="text-sm font-semibold uppercase text-primary mb-1">
        Aktuelle Einblicke, Projekte und Highlights
      </p>
    </div>

    <!-- Loading skeleton -->
    <div
        v-if="isLoading"
        class="flex gap-4 px-4"
    >
      <div
          v-for="n in 5"
          :key="n"
          class="shrink-0 w-60 h-60 rounded-xl bg-gray-200 animate-pulse"
      />
    </div>

    <!-- Error state -->
    <div
        v-else-if="status === 'error'"
        class="container text-muted text-sm"
    >
      Instagram-Feed konnte nicht geladen werden.
    </div>

    <!-- Carousel -->
    <UCarousel
        v-else-if="posts.length"
        v-slot="{ item }: { item: InstagramPost }"
        :items="posts"
        :ui="{
          item: 'basis-xs sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5 ps-0 mx-2 rounded-xl overflow-hidden shrink-0',
          container: 'ms-0',
          viewport: 'overflow-visible',
          dot: 'bg-secondary/50 data-[state=active]:bg-secondary',
          dots: '-bottom-12'
        }"
        class="px-2"
        dots
        wheel-gestures
    >
      <a
          :aria-label="`Instagram Post vom ${formatDate(item.timestamp)}`"
          :href="item.permalink"
          class="block aspect-square w-full overflow-hidden rounded-xl group relative"
          rel="noopener noreferrer"
          target="_blank"
      >
        <img
            :alt="`Instagram Post vom ${formatDate(item.timestamp)}`"
            :src="item.media_type === 'VIDEO' ? (item.thumbnail_url ?? item.media_url) : item.media_url"
            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
        >

        <!-- Play-badge for videos -->
        <div
            v-if="item.media_type === 'VIDEO'"
            class="absolute top-2 right-2 z-10 bg-black/50 rounded-full p-1 pointer-events-none"
        >
          <UIcon class="w-4 h-4 !text-white" name="lucide:play"/>
        </div>

        <!-- Caption – visible by default, hidden on hover -->
        <div
            v-if="item.caption"
            class="absolute inset-0 flex flex-col justify-end pointer-events-none
                   opacity-100 group-hover:opacity-0 transition-opacity duration-300 z-10"
        >
          <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"/>
          <p class="relative z-20 px-3 pb-3 text-xs leading-snug overflow-hidden line-clamp-3 !text-white truncate">
            {{ item.caption }}
          </p>
        </div>

        <!-- Hover overlay – brand CTA -->
        <div
            class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none
                   opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
        >
          <span class="brand-overlay is-duo" style="--overlay-alpha-top: 0.55; --overlay-alpha-bot: 0.82;"/>
          <UIcon class="w-16 h-16 drop-shadow relative z-20 !text-white" name="lucide:instagram"/>
          <span class="flex items-center gap-1 text-lg font-semibold tracking-wide drop-shadow relative z-20 mt-2 !text-white">
            Auf Instagram ansehen
            <UIcon class="w-3 h-3 !text-white" name="lucide:external-link"/>
          </span>
        </div>
      </a>
    </UCarousel>
  </section>
</template>

<script lang="ts" setup>
const {data, status} = await useLazyFetch<InstagramPost[]>('/api/instagram', {
  server: false,
  default: () => [],
});

const isLoading = computed(() => status.value === 'idle' || status.value === 'pending')

const posts = computed(() => data.value ?? []);

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(new Date(iso));
}
</script>
